<template>
  <!-- Teleported for the same reason as the chart viewer: a table too wide for the panel
       should get the whole screen, not the panel's. -->
  <Teleport to="body">
    <div
      ref="root"
      class="table-lightbox"
      role="dialog"
      aria-modal="true"
      :aria-label="title || 'Table'"
      tabindex="-1"
      @keydown.esc.stop="$emit('close')"
      @click.self="$emit('close')"
    >
      <div class="table-lightbox-bar">
        <p class="table-lightbox-title">{{ title || "Table" }}</p>
        <span v-if="note" class="table-lightbox-note">{{ note }}</span>
        <button
          type="button"
          class="table-lightbox-btn"
          title="Close"
          @click="$emit('close')"
        >
          <i class="fas fa-xmark"></i>
        </button>
      </div>

      <div class="table-lightbox-stage" @click.self="$emit('close')">
        <!-- The card's own rows, already sanitised when the card was rendered. -->
        <div class="table-lightbox-sheet" :style="themeVars">
          <div class="table-card" @click="onClick">
            <div class="table-card__scroll" tabindex="0" v-html="html"></div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { mapState } from "vuex";
import { buildThemeVars } from "@/components/OliverBotV2/theme/themes.js";

export default {
  name: "TableLightbox",
  props: {
    title: {
      type: String,
      default: "",
    },
    /** The card's table, as the card shows it. */
    html: {
      type: String,
      required: true,
    },
    /** What the card says of its rows, e.g. "First 200 of 1,240 rows". */
    note: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "cite"],
  computed: {
    ...mapState({
      oliverTheme: (state) => state.oliverChatBotV2.theme,
      oliverDarkMode: (state) => state.oliverChatBotV2.darkMode,
    }),
    // Outside the panel, so the theme is applied here rather than inherited.
    themeVars() {
      return buildThemeVars(this.oliverTheme, this.oliverDarkMode);
    },
  },
  mounted() {
    this.$refs.root?.focus();
  },
  methods: {
    onClick(event) {
      const marker = event.target.closest?.("[data-citation]");
      if (marker) this.$emit("cite", Number(marker.dataset.citation));
    },
  },
};
</script>

<style lang="scss" scoped>
@use "../theme/table-card" as *;

.table-lightbox {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  background: rgba(12, 10, 9, 0.95);

  &:focus {
    outline: none;
  }
}

.table-lightbox-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.table-lightbox-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-lightbox-note {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.table-lightbox-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.table-lightbox-stage {
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 16px;
}

.table-lightbox-sheet {
  flex: 1;
  min-width: 0;
  display: flex;
  border-radius: 12px;
  background: var(--oliver-panel-bg);
  color: var(--oliver-text);
  font-size: 15px;
}

// The rows arrive as HTML, so the card's rules reach them through :deep, as in the bubble.
:deep(.table-lightbox-sheet) {
  @include table-card;

  .table-card {
    flex: 1;
    display: flex;
    min-width: 0;
    margin: 0;
  }

  // The whole screen is the table's, so it scrolls in both directions up to its edges.
  .table-card__scroll {
    flex: 1;
    max-height: none;
  }
}
</style>
